import React, { useEffect, useMemo } from 'react'
import { useTable, usePagination, useSortBy } from 'react-table'

import TableHeader from '../shared/TableHeader'
import TableFooter from './TableFooter'
import TableBody from '../shared/TableBody'
import TableSettings from './TableSettings'
import { convertFromParams } from '../../../../utils/table/sortBy'
import getAppliedFilterString from '../../../../utils/table/getAppliedFilterString'
import withConfig from '../../../wrappers/withConfig'

const Table = ({
    name,
    columns,
    data,
    loading,
    pageCount: controlledPageCount,
    resultsCount,
    filterExpanded,
    settingsExpanded,
    filterFields,
    setParams,
    params,
    config,
    promotedRecords
}) => {
    const initialPageIndex = (params && params[name]) ? Number(params[name].Page) - 1 : 0
    const initialPageSize = (params && params[name]) ? Number(params[name].PageSize) : 50
    const initialSortBy = (params && params[name]) ? convertFromParams(params[name].SortByField, params[name].SortOrder) : []
    const initialPage = (params && params[name]) ? params[name].Page : null
    const { ID_COLUMN } = config
    const hiddenColumns = useMemo(() => [ID_COLUMN, ...columns.filter(x => x.hidden).map(x => x.accessor)], [columns.length])
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        preSortedRows,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        setHiddenColumns,
        // Get the state from the instance
        state: { pageIndex, pageSize, sortBy },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: initialSortBy
            }, // Pass our hoisted table state
            manualPagination: true, // pagination
            manualSorting: true,
            autoResetSortBy: false,
            disableSortRemove: true,
            pageCount: controlledPageCount,
            autoResetHiddenColumns: false
        },
        useSortBy,
        usePagination
    )

    useEffect(() => {
        setHiddenColumns(hiddenColumns)
    }, [hiddenColumns])

    // page info fetch
    useEffect(() => {
        const sortByProps = sortBy.length
            ? {
                  SortByField: `${sortBy[0].id}`,
                  SortOrder: sortBy[0].desc ? 'DESC' : 'ASC',
              }
            : {}
        const newParams = {
            ...params[name], Page: ( pageIndex + 1 ), PageSize: pageSize, ...sortByProps 
        }
        setParams(newParams, name)
    }, [pageIndex, pageSize, sortBy])

    useEffect(() => {
        if (initialPage) {
            gotoPage(initialPage - 1)
        } 
    }, [initialPage])

    // Render the UI for your table
    return (
        <>
            <div
                className={`table-container ${
                    filterExpanded ? 'is-hidden' : 'is-visible'
                } explorerTableWrapper`}
            >
                <TableSettings settingsExpanded={settingsExpanded} idColumn={ID_COLUMN} setHiddenColumns={setHiddenColumns} columns={columns} initialHiddenColumns={hiddenColumns} filterFields={filterFields} />
                <div className="filterSummaryWrapper">
                    <div className="filterSummaryText">
                        {getAppliedFilterString(params[name], filterFields)}
                    </div>
                </div>
                <table
                    {...getTableProps()}
                    className="table is-striped is-fullwidth is-hoverable explorerTable"
                >
                    <TableHeader headerGroups={headerGroups} />
                    <TableBody
                        {...{
                            getTableBodyProps,
                            page: promotedRecords ? preSortedRows : page,
                            prepareRow,
                            loading,
                            controlledPageCount,
                            pageSize,
                            ID_COLUMN
                        }}
                    />
                </table>
            </div>
            <TableFooter
                {...{
                    filterExpanded,
                    gotoPage,
                    previousPage,
                    canPreviousPage,
                    nextPage,
                    canNextPage,
                    pageCount,
                    resultsCount,
                    pageOptions,
                    pageIndex,
                    pageSize,
                    setPageSize,
                    loading,
                }}
            />
        </>
    )
}

export default withConfig(Table)
